import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { api, CandidateRecommendation } from '../../../services/api'
import { ScoreBreakdownCard } from '../../../components/shared/ScoreBreakdownCard'
import { recordRecommendationAssigned } from './recordRecommendationDecision'

// Styled Components
const Container = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`

const Header = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primaryDark || '#1a237e'});
  color: white;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  display: flex;
  align-items: center;
  gap: 8px;
`

const RefreshButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
`

const RoleColumn = styled.div<{ $filled: boolean }>`
  background: white;
  border: 2px solid ${({ $filled, theme }) => $filled ? theme.colors.success : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  transition: border-color 0.3s;
`

const RoleHeader = styled.div<{ $filled: boolean }>`
  background: ${({ $filled, theme }) => $filled ? `${theme.colors.success}15` : `${theme.colors.primary}10`};
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const RoleTitle = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.md};
  display: flex;
  align-items: center;
  gap: 8px;
`

const RoleStats = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 4px;
`

const RoleBody = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  max-height: 400px;
  overflow-y: auto;
`

const SectionLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  margin-bottom: 8px;
  margin-top: 12px;

  &:first-child {
    margin-top: 0;
  }
`

const AssignedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const AssignedMember = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: ${({ theme }) => `${theme.colors.success}10`};
  border: 1px solid ${({ theme }) => `${theme.colors.success}30`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const MemberName = styled.span`
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const LoadInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  text-align: center;
`

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.danger};
  cursor: pointer;
  font-size: 1.3rem;
  padding: 0 4px;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`

const CandidatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const CandidateCard = styled.div<{ $isAssignedElsewhere?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 12px;
  transition: all 0.2s;
  opacity: ${({ $isAssignedElsewhere }) => $isAssignedElsewhere ? 0.5 : 1};
  background: ${({ $isAssignedElsewhere }) => $isAssignedElsewhere ? '#f5f5f5' : 'white'};

  &:hover {
    border-color: ${({ theme, $isAssignedElsewhere }) => $isAssignedElsewhere ? theme.colors.border : theme.colors.primary};
  }
`

const CandidateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const CandidateName = styled.div`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const CandidateRole = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const ScoreBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const ScoreValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}15`};
  padding: 4px 8px;
  border-radius: 4px;
`

const TierBadge = styled.span<{ $tier: number }>`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  background: ${({ $tier }) => {
        switch ($tier) {
            case 1: return '#4CAF50';
            case 2: return '#8BC34A';
            case 3: return '#FFC107';
            case 4: return '#FF9800';
            case 5: return '#F44336';
            default: return '#9E9E9E';
        }
    }};
  color: white;
`

const CandidateDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
`

const DetailChip = styled.span<{ $type: string }>`
  font-size: 10px;
  padding: 3px 6px;
  border-radius: 4px;
  background: ${({ $type }) => {
        switch ($type) {
            case 'tecnologia': return '#E8F5E9';
            case 'sector': return '#E3F2FD';
            case 'experiencia': return '#FFF3E0';
            case 'disponibilidad': return '#F3E5F5';
            case 'penalizacion': return '#FFEBEE';
            case 'assigned': return '#E3F2FD';
            default: return '#ECEFF1';
        }
    }};
  color: ${({ $type }) => {
        switch ($type) {
            case 'tecnologia': return '#2E7D32';
            case 'sector': return '#1565C0';
            case 'experiencia': return '#EF6C00';
            case 'disponibilidad': return '#7B1FA2';
            case 'penalizacion': return '#C62828';
            case 'assigned': return '#1565C0';
            default: return '#546E7A';
        }
    }};
`

const AssignButton = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark || '#1a237e'};
    transform: translateY(-1px);
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.textMuted};
    cursor: not-allowed;
    transform: none;
  }
`

const BreakdownToggle = styled.button`
  background: transparent;
  border: 1px dashed ${({ theme }) => `${theme.colors.primary}50`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 10px;
  padding: 4px 8px;
  margin-top: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &:hover {
    background: ${({ theme }) => `${theme.colors.primary}10`};
  }
`

const BreakdownWrapper = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => $isOpen ? '400px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 30px;
  color: ${({ theme }) => theme.colors.textMuted};
`

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const SuccessState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const ErrorMessage = styled.div`
  padding: 12px;
  background: ${({ theme }) => `${theme.colors.danger}10`};
  border: 1px solid ${({ theme }) => `${theme.colors.danger}30`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin-bottom: 12px;
`

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`

const InternBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #9c27b0;
  color: white;
  font-size: 9px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  margin-left: 4px;
`

const ProgressBar = styled.div`
  background: ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  height: 6px;
  margin-top: 8px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ $percent: number }>`
  background: ${({ $percent, theme }) => $percent >= 100 ? theme.colors.success : theme.colors.primary};
  height: 100%;
  width: ${({ $percent }) => Math.min($percent, 100)}%;
  transition: width 0.3s ease;
`

// Types
interface TeamAssignment {
    userId: string
    userName: string
    load: number
    role?: string
    horasPerfil?: number
}

interface RoleRequirement {
    role: string
    count: number
    cargabilidad: number
    tecnologias?: string[]
    certificates?: string[]
    start_availability?: string
}

interface HiringNeedInfo {
    role: string
    necesita_contratacion: boolean
    rebalanceo_sugerido: boolean
    mensaje?: string
    candidatos_sin_disponibilidad: number
    candidatos_con_perfil_parcial: number
    accion_sugerida?: 'rebalanceo' | 'contratacion' | 'ninguna'
    candidatos_ocupados?: CandidateRecommendation[]
}

interface CandidatesByRole {
    [role: string]: CandidateRecommendation[]
}

interface UnifiedTeamRecommendationsProps {
    opportunityId: number | undefined
    proyectoEstado?: string | null
    roleRequirements: RoleRequirement[]
    assignedMembers: TeamAssignment[]
    onAssign: (userId: number, displayName: string, role: string, cargabilidad: number) => void
    onRemove: (userId: string) => void
    onUpdateLoad: (userId: string, load: number) => void
    onUpdateHours?: (userId: string, hours: number | undefined) => void
    onOpenFullModal?: (role?: string) => void
}

const VALID_PROJECT_STATES = ['en_curso', 'planificacion', 'pendiente']

export const UnifiedTeamRecommendations: React.FC<UnifiedTeamRecommendationsProps> = ({
    opportunityId,
    proyectoEstado,
    roleRequirements,
    assignedMembers,
    onAssign,
    onRemove,
    onUpdateLoad,
    onUpdateHours,
    onOpenFullModal,
}) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [candidatesByRole, setCandidatesByRole] = useState<CandidatesByRole>({})
    const [hiringNeedsByRole, setHiringNeedsByRole] = useState<{ [role: string]: HiringNeedInfo }>({})
    const [expandedBreakdowns, setExpandedBreakdowns] = useState<Set<number>>(new Set())
    const [incluirPracticas, setIncluirPracticas] = useState(false)

    // Get all assigned user IDs across all roles
    const allAssignedIds = new Set(assignedMembers.map(m => parseInt(m.userId)))

    // Calculate total positions and filled positions
    const totalPositions = roleRequirements.reduce((sum, r) => sum + r.count, 0)
    const filledPositions = assignedMembers.length
    const allFilled = filledPositions >= totalPositions

    const canFetchRecommendations = opportunityId && roleRequirements.length > 0
        && proyectoEstado && VALID_PROJECT_STATES.includes(proyectoEstado)

    const fetchAllRecommendations = useCallback(async () => {
        if (!canFetchRecommendations) return

        setLoading(true)
        setError(null)

        try {
            // Fetch recommendations for all roles in parallel
            const results = await Promise.all(
                roleRequirements.map(async (req) => {
                    try {
                        const result = await api.recommendations.getCandidates({
                            opportunity_id: opportunityId,
                            rol: req.role,
                            cargabilidad: req.cargabilidad,
                            tecnologias: req.tecnologias || [],
                            // Role-level deferred start — backend shifts the availability
                            // window by this offset on top of the opportunity's start date.
                            start_availability: req.start_availability || 'Inmediato',
                            incluir_practicas: incluirPracticas,  // Pass the checkbox value
                        })
                        return {
                            role: req.role,
                            candidates: result.data.candidatos,
                            hiringNeed: {
                                role: req.role,
                                necesita_contratacion: result.data.necesita_contratacion,
                                rebalanceo_sugerido: result.data.rebalanceo_sugerido,
                                mensaje: result.data.capacidad_insuficiente_mensaje,
                                candidatos_sin_disponibilidad: result.data.candidatos_sin_disponibilidad,
                                candidatos_con_perfil_parcial: result.data.candidatos_con_perfil_parcial,
                                accion_sugerida: result.data.resumen_capacidad?.accion_sugerida,
                                candidatos_ocupados: result.data.candidatos_ocupados || [],
                            } as HiringNeedInfo,
                        }
                    } catch (err) {
                        console.error(`Error fetching for role ${req.role}:`, err)
                        return {
                            role: req.role,
                            candidates: [],
                            hiringNeed: {
                                role: req.role,
                                necesita_contratacion: false,
                                rebalanceo_sugerido: false,
                                candidatos_sin_disponibilidad: 0,
                                candidatos_con_perfil_parcial: 0,
                            },
                        }
                    }
                })
            )

            // Organize by role
            const byRole: CandidatesByRole = {}
            const hiringByRole: { [role: string]: HiringNeedInfo } = {}
            results.forEach(({ role, candidates, hiringNeed }) => {
                byRole[role] = candidates
                hiringByRole[role] = hiringNeed
            })

            setCandidatesByRole(byRole)
            setHiringNeedsByRole(hiringByRole)
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error al obtener recomendaciones')
        } finally {
            setLoading(false)
        }
    }, [canFetchRecommendations, JSON.stringify(roleRequirements), incluirPracticas])

    useEffect(() => {
        if (canFetchRecommendations) {
            fetchAllRecommendations()
        } else {
            // Clear recommendations when no opportunity, no roles, or invalid project state
            setCandidatesByRole({})
        }
    }, [fetchAllRecommendations])

    const handleAssign = (candidate: CandidateRecommendation, role: string, cargabilidad: number) => {
        onAssign(candidate.user_id, candidate.display_name, role, cargabilidad)
        void recordRecommendationAssigned(opportunityId, candidate.user_id, candidate.history_id)
        // Refresh after a short delay
        setTimeout(fetchAllRecommendations, 300)
    }

    const toggleBreakdown = (userId: number) => {
        setExpandedBreakdowns(prev => {
            const newSet = new Set(prev)
            if (newSet.has(userId)) {
                newSet.delete(userId)
            } else {
                newSet.add(userId)
            }
            return newSet
        })
    }

    const getTierIcon = (tier: number) => {
        switch (tier) {
            case 1: return '🎯'
            case 2: return '⭐'
            case 3: return '✓'
            case 4: return '~'
            case 5: return '⚠️'
            default: return ''
        }
    }

    const getRoleAssignedMembers = (role: string) => {
        return assignedMembers.filter(m => m.role === role)
    }

    const isAssignedToOtherRole = (userId: number, currentRole: string) => {
        return assignedMembers.some(m => parseInt(m.userId) === userId && m.role !== currentRole)
    }

    const getAssignedRole = (userId: number): string | null => {
        const member = assignedMembers.find(m => parseInt(m.userId) === userId)
        return member?.role || null
    }

    if (roleRequirements.length === 0) {
        return (
            <EmptyState>
                💡 Define roles requeridos en la sección de Oferta para ver recomendaciones de candidatos.
            </EmptyState>
        )
    }

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    🎯 Recomendaciones de Equipo
                    <span style={{ fontWeight: 'normal', fontSize: '0.85em', opacity: 0.9 }}>
                        ({filledPositions}/{totalPositions} posiciones)
                    </span>
                </HeaderTitle>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <CheckboxLabel title="Incluir usuarios en prácticas en las recomendaciones">
                        <input
                            type="checkbox"
                            checked={incluirPracticas}
                            onChange={(e) => setIncluirPracticas(e.target.checked)}
                        />
                        Incluir prácticas
                    </CheckboxLabel>
                    {onOpenFullModal && (
                        <RefreshButton onClick={() => onOpenFullModal()} title="Abrir recomendador completo">
                            🔍 Ver más
                        </RefreshButton>
                    )}
                    <RefreshButton onClick={fetchAllRecommendations} disabled={loading}>
                        {loading ? '⏳ Cargando...' : '🔄 Actualizar'}
                    </RefreshButton>
                </div>
            </Header>

            {error && <ErrorMessage style={{ margin: '16px' }}>{error}</ErrorMessage>}

            <RolesGrid>
                {roleRequirements.map((req) => {
                    const roleAssigned = getRoleAssignedMembers(req.role)
                    const isFilled = roleAssigned.length >= req.count
                    const candidates = candidatesByRole[req.role] || []
                    // Filter out already assigned to THIS role
                    const availableCandidates = candidates.filter(
                        c => !roleAssigned.some(m => parseInt(m.userId) === c.user_id)
                    )

                    return (
                        <RoleColumn key={req.role} $filled={isFilled}>
                            <RoleHeader $filled={isFilled}>
                                <RoleTitle>
                                    {isFilled ? '✅' : '👤'} {req.role || 'Sin especificar'}
                                </RoleTitle>
                                <RoleStats>
                                    {roleAssigned.length}/{req.count} asignados • Carga: {req.cargabilidad}%
                                </RoleStats>
                                <ProgressBar>
                                    <ProgressFill $percent={(roleAssigned.length / req.count) * 100} />
                                </ProgressBar>
                            </RoleHeader>

                            <RoleBody>
                                {/* Assigned Members */}
                                {roleAssigned.length > 0 && (
                                    <>
                                        <SectionLabel>✓ Asignados</SectionLabel>
                                        <AssignedList>
                                            {roleAssigned.map(member => (
                                                <AssignedMember key={member.userId}>
                                                    <MemberInfo>
                                                        <MemberName>{member.userName}</MemberName>
                                                        <LoadInput
                                                            type="number"
                                                            min="0"
                                                            value={member.horasPerfil || ''}
                                                            onChange={(e) => onUpdateHours?.(member.userId, parseInt(e.target.value) || undefined)}
                                                            placeholder="h"
                                                            title="Horas (entrada manual)"
                                                            style={{ width: '50px' }}
                                                        />
                                                        <span style={{ fontSize: '12px', color: '#666' }}>h</span>
                                                        <LoadInput
                                                            type="number"
                                                            value={member.load}
                                                            readOnly
                                                            title="Carga media calculada"
                                                            style={{ width: '46px', background: '#f5f5f5' }}
                                                        />
                                                        <span style={{ fontSize: '12px', color: '#666' }}>%</span>
                                                    </MemberInfo>
                                                    <RemoveBtn onClick={() => onRemove(member.userId)} title="Quitar">
                                                        ×
                                                    </RemoveBtn>
                                                </AssignedMember>
                                            ))}
                                        </AssignedList>
                                    </>
                                )}

                                {/* Candidates - only show if not filled */}
                                {!isFilled && (
                                    <>
                                        <SectionLabel>🎯 Candidatos Recomendados</SectionLabel>

                                        {loading && <LoadingState>Cargando...</LoadingState>}

                                        {!loading && availableCandidates.length === 0 && (
                                            <>
                                                {hiringNeedsByRole[req.role]?.rebalanceo_sugerido ? (
                                                    <>
                                                        <div style={{
                                                            background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
                                                            border: '1px solid #FF9800',
                                                            borderRadius: '8px',
                                                            padding: '12px',
                                                            marginTop: '8px',
                                                        }}>
                                                            <div style={{ fontWeight: 600, color: '#E65100', marginBottom: '4px' }}>
                                                                🔄 Rebalanceo Sugerido
                                                            </div>
                                                            <div style={{ fontSize: '0.85em', color: '#BF360C' }}>
                                                                {hiringNeedsByRole[req.role]?.mensaje ||
                                                                    `Hay ${hiringNeedsByRole[req.role]?.candidatos_sin_disponibilidad || 0} candidatos con perfil adecuado pero ocupados. Considere redistribuir cargas de trabajo.`}
                                                            </div>
                                                        </div>
                                                        {/* Show busy candidates for visibility */}
                                                        {(hiringNeedsByRole[req.role]?.candidatos_ocupados || []).length > 0 && (
                                                            <>
                                                                <SectionLabel style={{ marginTop: '12px' }}>👥 Candidatos ocupados con perfil adecuado</SectionLabel>
                                                                <CandidatesList>
                                                                    {(hiringNeedsByRole[req.role]?.candidatos_ocupados || []).slice(0, 5).map(candidate => (
                                                                        <CandidateCard key={candidate.user_id} $isAssignedElsewhere={true}>
                                                                            <CandidateHeader>
                                                                                <div>
                                                                                    <CandidateName>{candidate.display_name}</CandidateName>
                                                                                    <CandidateRole>
                                                                                        {candidate.job_role || 'Sin rol definido'}
                                                                                    </CandidateRole>
                                                                                </div>
                                                                                <ScoreBadge>
                                                                                    <span style={{
                                                                                        fontSize: '0.75rem',
                                                                                        padding: '4px 8px',
                                                                                        borderRadius: '4px',
                                                                                        fontWeight: 600,
                                                                                        background: '#F44336',
                                                                                        color: 'white',
                                                                                    }}>
                                                                                        Ocupado {Math.max(0, 100 - candidate.disponibilidad_periodo).toFixed(0)}%
                                                                                    </span>
                                                                                </ScoreBadge>
                                                                            </CandidateHeader>
                                                                            <CandidateDetails>
                                                                                <DetailChip $type="disponibilidad">
                                                                                    📅 {candidate.disponibilidad_periodo.toFixed(0)}% disp.
                                                                                </DetailChip>
                                                                                {candidate.tecnologias_coincidentes.slice(0, 2).map(tech => (
                                                                                    <DetailChip key={tech} $type="tecnologia">
                                                                                        💻 {tech}
                                                                                    </DetailChip>
                                                                                ))}
                                                                                {candidate.match_sector && (
                                                                                    <DetailChip $type="sector">🏢 Sector</DetailChip>
                                                                                )}
                                                                            </CandidateDetails>
                                                                            <AssignButton
                                                                                onClick={() => handleAssign(candidate, req.role, req.cargabilidad)}
                                                                                title="Asignar de todas formas (requiere rebalanceo previo)"
                                                                            >
                                                                                ⚠️ Asignar (requiere rebalanceo)
                                                                            </AssignButton>
                                                                        </CandidateCard>
                                                                    ))}
                                                                </CandidatesList>
                                                            </>
                                                        )}
                                                    </>
                                                ) : hiringNeedsByRole[req.role]?.necesita_contratacion ? (
                                                    <div style={{
                                                        background: 'linear-gradient(135deg, #FFEBEE, #FFCDD2)',
                                                        border: '1px solid #EF5350',
                                                        borderRadius: '8px',
                                                        padding: '12px',
                                                        marginTop: '8px',
                                                    }}>
                                                        <div style={{ fontWeight: 600, color: '#C62828', marginBottom: '4px' }}>
                                                            ⚠️ Necesidad de Contratación
                                                        </div>
                                                        <div style={{ fontSize: '0.85em', color: '#B71C1C' }}>
                                                            {hiringNeedsByRole[req.role]?.mensaje ||
                                                                'No se encontraron candidatos con el perfil requerido ni habilidades relacionadas. Se recomienda iniciar proceso de contratación.'}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <EmptyState>
                                                        No hay candidatos disponibles.
                                                        <br />
                                                        <small>Guarda la oportunidad o ajusta los requisitos.</small>
                                                    </EmptyState>
                                                )}
                                            </>
                                        )}

                                        {!loading && availableCandidates.length > 0 && (
                                            <CandidatesList>
                                                {availableCandidates.slice(0, 5).map(candidate => {
                                                    const assignedElsewhere = isAssignedToOtherRole(candidate.user_id, req.role)
                                                    const existingRole = getAssignedRole(candidate.user_id)

                                                    return (
                                                        <CandidateCard key={candidate.user_id} $isAssignedElsewhere={assignedElsewhere}>
                                                            <CandidateHeader>
                                                                <div>
                                                                    <CandidateName>{candidate.display_name}</CandidateName>
                                                                    <CandidateRole>
                                                                        {candidate.job_role || 'Sin rol definido'}
                                                                    </CandidateRole>
                                                                </div>
                                                                <ScoreBadge>
                                                                    <ScoreValue>{candidate.score_final.toFixed(1)}</ScoreValue>
                                                                    <TierBadge $tier={candidate.tier}>
                                                                        {getTierIcon(candidate.tier)} T{candidate.tier}
                                                                    </TierBadge>
                                                                </ScoreBadge>
                                                            </CandidateHeader>

                                                            <CandidateDetails>
                                                                {assignedElsewhere && existingRole && (
                                                                    <DetailChip $type="assigned">
                                                                        ⚡ Asignado a {existingRole}
                                                                    </DetailChip>
                                                                )}
                                                                <DetailChip $type="disponibilidad">
                                                                    📅 {candidate.disponibilidad_periodo.toFixed(0)}%
                                                                </DetailChip>
                                                                {candidate.tecnologias_coincidentes.slice(0, 2).map(tech => (
                                                                    <DetailChip key={tech} $type="tecnologia">
                                                                        💻 {tech}
                                                                    </DetailChip>
                                                                ))}
                                                                {candidate.match_sector && (
                                                                    <DetailChip $type="sector">🏢 Sector</DetailChip>
                                                                )}
                                                                {candidate.years_experience && (
                                                                    <DetailChip $type="experiencia">
                                                                        📊 {candidate.years_experience}a
                                                                    </DetailChip>
                                                                )}
                                                            </CandidateDetails>

                                                            {candidate.score_breakdown && (
                                                                <>
                                                                    <BreakdownToggle onClick={() => toggleBreakdown(candidate.user_id)}>
                                                                        {expandedBreakdowns.has(candidate.user_id) ? '▼' : '▶'}
                                                                        {candidate.score_breakdown.formula_display}
                                                                    </BreakdownToggle>
                                                                    <BreakdownWrapper $isOpen={expandedBreakdowns.has(candidate.user_id)}>
                                                                        <ScoreBreakdownCard
                                                                            breakdown={candidate.score_breakdown}
                                                                            compact={true}
                                                                        />
                                                                    </BreakdownWrapper>
                                                                </>
                                                            )}

                                                            <AssignButton
                                                                onClick={() => handleAssign(candidate, req.role, req.cargabilidad)}
                                                                disabled={assignedElsewhere}
                                                                title={assignedElsewhere ? `Ya asignado a ${existingRole || 'otro rol'}` : 'Asignar a este rol'}
                                                            >
                                                                {assignedElsewhere ? (
                                                                    `⚠️ Ya en ${existingRole || 'otro rol'}`
                                                                ) : (
                                                                    <>✓ Asignar como {req.role}</>
                                                                )}
                                                            </AssignButton>
                                                        </CandidateCard>
                                                    )
                                                })}
                                            </CandidatesList>
                                        )}
                                    </>
                                )}

                                {isFilled && (
                                    <SuccessState>
                                        ✅ Todas las posiciones cubiertas
                                    </SuccessState>
                                )}
                            </RoleBody>
                        </RoleColumn>
                    )
                })}
            </RolesGrid>

            {allFilled && (
                <div style={{
                    background: '#E8F5E9',
                    padding: '16px',
                    textAlign: 'center',
                    borderTop: '1px solid #C8E6C9'
                }}>
                    <span style={{ fontSize: '1.2em' }}>🎉</span>
                    <strong style={{ marginLeft: '8px', color: '#2E7D32' }}>
                        ¡Equipo completo! Todas las posiciones han sido asignadas.
                    </strong>
                </div>
            )}
        </Container>
    )
}

export default UnifiedTeamRecommendations
